import { flow, getParent, getRoot } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';

import { PLATFORM } from '@lusha/source-platforms';

import { editedContactAdapter } from '../../../API/adapters';
import { editContactData } from '../../../API/editContactAPI';
import { updateWishedContact } from '../../../API/wishlistApi';
import { notifyFail, notifySuccess } from '../../../hooks/useNotification';
import reportClientEvent from '../../../utils/BIEvents/reportClientEvent';
import { ADDABLE_KEYS } from '../EditContact/consts';

import { getContactAddedData } from './utils';

export const actions = self => ({
  setContactKeyBeingAdded(key, label = null) {
    self.contactKeyBeingAdded = key;
    self.inputValue = '';
    self.keyLabel = label;
  },
  setInputValue(value) {
    self.inputValue = value;
  },
  setWishlistAddedData(response) {
    const contact = getParent(self);
    contact.setWishlistAddedData(response);
  },
  setResponseValues(response) {
    const { savedTab } = getRoot(self);
    const contact = getParent(self);
    const contactData = editedContactAdapter(response);
    contact.setContactEditableData(contactData);

    // Sync contact data across to saved tab;
    const contactInSavedTab = savedTab?.savedContacts.get(response.id);
    if (contactInSavedTab) {
      contactInSavedTab.setContactEditableData(contactData);
    }
  },
  reportDataAddedClientEvent({ inputValue, itemType, isWishlist = null, isBulkView = false }) {
    const { context: { url }, userSettings: { platform } } = getRoot(self);
    const contact = getParent(self);

    reportClientEvent({
      subPlatform: platform,
      pageUrl: url,
      eventType: inputValue ? 'contact_details_update_added' : 'contact_details_update_removed',
      eventValue: itemType,
      product: 'contact_details_update',
      eventAdditionalInfo: {
        isBulk: isBulkView,
        contactLinkedinUrl: contact.socialLink,
        companyLinkedinUrl: contact.company?.LinkedIn,
        isWishlist,
      },
    });
  },
  addContactData: flow(function* editContact(data) {
    try {
      const response = yield editContactData(data);
      self.setResponseValues(response);
      notifySuccess('Contact data added successfully');
    } catch (error) {
      notifyFail('Failed to add contact data');
    }
  }),
  addWishlistContactData: flow(function* editContact(data) {
    try {
      const response = yield updateWishedContact(data);
      self.setWishlistAddedData(response);
      notifySuccess('Contact data added successfully');
    } catch (error) {
      notifyFail('Failed to add contact data');
    }
  }),
  addPartialProfileContactData: flow(function* addContact(data, wishlistData) {
    try {
      yield editContactData(data);
      // For partial profiles, only update wishlistData (not regular contact data)
      const contact = getParent(self);
      contact.setWishlistAddedData(wishlistData);
      notifySuccess('Contact data added successfully');
    } catch (error) {
      notifyFail('Failed to add contact data');
    }
  }),

  onAddContactData() {
    const contact = getParent(self);

    if (contact.isWishlistContact) {
      self.onAddWishlistContactData();

      return;
    }
    if (!self.inputValue || self.hasInputValidationError) {
      self.setContactKeyBeingAdded(null);

      return;
    }

    const { app, userSettings } = getRoot(self);
    const { contactId } = contact;
    const { itemType, ...contactData } = getContactAddedData({
      contact,
      contactKey: self.contactKeyBeingAdded,
      inputValue: self.inputValue,
      label: self.keyLabel,
    });

    const data = {
      contactId,
      ...contactData,
      metadata: {
        sessionId: app.sessionId || uuid(),
        isRevert: false,
        itemType,
        platform: PLATFORM.PLUGIN,
        subPlatform: userSettings.platform,
      },
    };

    self.addContactData(data);
    self.reportDataAddedClientEvent({ inputValue: self.inputValue, itemType });
    self.setContactKeyBeingAdded(null);
    self.setInputValue('');
    self.keyLabel = null;
  },
  onAddWishlistContactData() {
    const { context: { isBulk }, app, userSettings } = getRoot(self);
    const contact = getParent(self);

    if (!self.inputValue || self.hasInputValidationError) {
      self.setContactKeyBeingAdded(null);

      return;
    }

    const isAddingEmail = self.contactKeyBeingAdded === ADDABLE_KEYS.email;
    const isAddingPhone = self.contactKeyBeingAdded === ADDABLE_KEYS.phone;

    const contactData = {
      emails: isAddingEmail ? [self.inputValue] : [],
      phones: isAddingPhone ? [self.inputValue] : [],
    };

    const itemType = isAddingEmail ? 'contact_email' : 'contact_phone';

    const emails = contact.wishlistData.emails.map(email => ({ value: email.value, type: email.type }));
    const phones = contact.wishlistData.phones.map(phone => ({ value: phone.value, type: phone.type }));

    contactData.emails.length > 0 && emails.push({ type: 'work', value: contactData.emails[0] });
    contactData.phones.length > 0 && phones.push({ type: 'work', value: contactData.phones[0] });

    if (contact.isPartialProfile) {
      // For partial profiles, save to /v4/edit-contacts with ALL emails and phones
      const emailsArray = emails.map(e => e.value);
      const phonesArray = phones.map(p => p.value);
      const emailsMetadata = emails.map(e => ({
        isManuallyAdded: true,
        label: e.type,
        isEdited: false,
      }));
      const phonesMetadata = phones.map(p => ({
        isManuallyAdded: true,
        type: p.type,
        isEdited: false,
      }));

      const data = {
        contactId: contact.contactId,
        emails: emailsArray,
        phones: phonesArray,
        emailsMetadata,
        phonesMetadata,
        metadata: {
          sessionId: app.sessionId || uuid(),
          isRevert: false,
          itemType,
          platform: PLATFORM.PLUGIN,
          subPlatform: userSettings.platform,
        },
      };

      self.addPartialProfileContactData(data, { emails, phones });
    } else {
      // For non-partial wishlist contacts, use wishlist API
      const wishlistContactData = { contactId: contact.wishlistData.id, data: { emails, phones } };
      self.addWishlistContactData(wishlistContactData);
    }
    self.reportDataAddedClientEvent({ inputValue: self.inputValue, itemType, isWishlist: true, isBulkView: isBulk });
    self.setContactKeyBeingAdded(null);
    self.setInputValue('');
    self.keyLabel = null;
  },
  onKeyDown(event) {
    const contact = getParent(self);

    if (event.key === 'Escape') {
      self.setContactKeyBeingAdded(null);
    } else if (event.key === 'Enter') {
      if (contact.isWishlistContact) {
        self.onAddWishlistContactData();
      } else {
        self.onAddContactData();
      }
    }
  },
});
